import { Box, SlideFade, ScaleFade, Fade } from '@chakra-ui/react'
import { useLocation } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'

/**
 * Page Transition Wrapper
 * Provides smooth animations when navigating between pages
 * Optimized for faster perceived loading
 * 
 * @param {Object} props
 * @param {string} props.type - Animation type: 'slide' | 'scale' | 'fade' | 'none'
 * @param {React.ReactNode} props.children - Page content
 */
export default function PageTransition({ 
  type = 'fade', // Changed default to fade - faster perceived load
  children,
  ...props 
}) {
  const location = useLocation()
  const [isVisible, setIsVisible] = useState(true) // Start visible for faster initial render
  const isFirstRender = useRef(true)

  // Trigger animation on route change (not initial mount)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return // Skip animation on first render for faster initial load
    }
    
    setIsVisible(false)
    // Minimal delay - just enough to trigger animation
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [location.pathname])

  // Skip animation entirely for fastest load
  if (type === 'none') {
    return <Box w="full">{children}</Box>
  }

  const transitionProps = {
    in: isVisible,
    unmountOnExit: false,
    ...props
  }

  switch (type) {
    case 'scale':
      return (
        <ScaleFade 
          {...transitionProps}
          initialScale={0.95}
        >
          <Box w="full">{children}</Box>
        </ScaleFade>
      )
    
    case 'fade':
      return (
        <Fade {...transitionProps}>
          <Box w="full">{children}</Box>
        </Fade>
      )
    
    case 'slide':
    default:
      return (
        <SlideFade 
          {...transitionProps}
          offsetY="20px"
        >
          <Box w="full">{children}</Box>
        </SlideFade>
      )
  }
}

/**
 * Section transition for animating content sections within a page
 * Useful for staggered animations
 */
export function SectionTransition({ 
  delay = 0, 
  children, 
  ...props 
}) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay)
    return () => clearTimeout(timer)
  }, [delay])

  return (
    <SlideFade 
      in={isVisible} 
      offsetY="30px"
      transition={{ enter: { duration: 0.4, delay: delay / 1000 } }}
      {...props}
    >
      {children}
    </SlideFade>
  )
}

/**
 * Staggered container for animating multiple children
 * Each child will animate with increasing delay
 */
export function StaggerContainer({ 
  children, 
  staggerDelay = 100,
  ...props 
}) {
  return (
    <Box {...props}>
      {Array.isArray(children) 
        ? children.map((child, index) => (
            <SectionTransition key={index} delay={index * staggerDelay}>
              {child}
            </SectionTransition>
          ))
        : children
      }
    </Box>
  )
}
